import { Component, EventEmitter, OnDestroy, OnInit, Output, signal } from '@angular/core'
import { BlobItem, BlobPrefix } from 'src/app/shared/models/blob-storage.model'
import { BlobStorageService } from 'src/app/shared/services/blob-storage.service'

@Component({
  selector: 'app-customer-download-control',
  templateUrl: './customer-download-control.component.html',
  styleUrls: ['./customer-download-control.component.scss'],
  standalone: false,
  providers: [BlobStorageService],
})
export class CustomerDownloadControlComponent implements OnDestroy, OnInit {
  @Output() historyClosed = new EventEmitter<void>()

  public blobs = signal<BlobItem[]>([])
  public folders = signal<BlobPrefix[]>([])
  public currentPath = signal<string[]>(['root'])
  public isLoadingBlobs = signal(false)
  public error = signal<string | null>(null)

  constructor(private blobStorage: BlobStorageService) {}

  ngOnInit(): void {
    this.loadDirectory()
  }

  ngOnDestroy(): void {
    this.blobStorage.destroy()
  }

  handleOnDownloadClosed = () => {
    this.historyClosed.emit()
  }

  async loadDirectory(): Promise<void> {
    this.isLoadingBlobs.set(true)
    this.error.set(null)
    this.blobs.set([])
    this.folders.set([])

    try {
      const prefix = this.getCurrentPrefix()
      const result = await this.blobStorage.listBlobs(prefix || undefined)
      this.folders.set(result.prefixes)
      this.blobs.set(result.blobs)
    } catch (err: any) {
      this.error.set(err?.message || 'Failed to load directory contents')
    } finally {
      this.isLoadingBlobs.set(false)
    }
  }

  navigateToFolder(folderName: string): void {
    // folderName from SDK is the full prefix relative to current position
    // Extract just the folder display name (last segment without trailing slash)
    const displayName = folderName.replace(/\/$/, '').split('/').pop() || folderName
    this.currentPath.update((path) => [...path, displayName])
    this.loadDirectory()
  }

  navigateUp(): void {
    if (this.currentPath().length > 1) {
      this.currentPath.update((path) => path.slice(0, -1))
      this.loadDirectory()
    }
  }

  navigateToPathSegment(index: number): void {
    if (index < this.currentPath().length - 1) {
      this.currentPath.update((path) => path.slice(0, index + 1))
      this.loadDirectory()
    }
  }

  async downloadFile(blobName: string): Promise<void> {
    try {
      await this.blobStorage.downloadFile(blobName)
    } catch (err: any) {
      this.error.set(err?.message || 'Failed to download file')
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B'
    const units = ['B', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    const size = bytes / Math.pow(1024, i)
    return `${size < 10 ? size.toFixed(1) : Math.round(size)} ${units[i]}`
  }

  private getCurrentPrefix(): string {
    // Path segments after the container name (first element) form the blob prefix
    if (this.currentPath().length <= 1) {
      return ''
    }
    return this.currentPath().slice(1).join('/') + '/'
  }
}
